import { useEffect, useState } from "react";
import { Card } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../components/ui/table";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faSpinner,
  faEdit,
  faToggleOn,
  faToggleOff,
  faEye,
  faFilter,
} from "@fortawesome/free-solid-svg-icons";
import SearchBar from "../../components/ui/SearchBar";
import EmptyState from "../../components/ui/EmptyState";
import Pagination from "../../components/ui/Pagination";
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from "../../components/ui/select";
import { usePermissions } from "../../hooks/usePermissions";
import { toastSuccess, toastError } from "../../utils/toast";
import ViewPermissionModal from "./user/ViewPermissionModal";

const ManagePermissionA = () => {
  const {
    permissions,
    pagination,
    loading,
    error,
    modules,
    searchTerm,
    setSearchTerm,
    moduleFilter,
    setModuleFilter,
    currentPage,
    setCurrentPage,
    pageSize,
    setPageSize,
    refresh,
    fetchModules,
    assignPermission,
    revokePermission,
  } = usePermissions();

  const [selectedPermission, setSelectedPermission] = useState(null);
  const [showViewModal, setShowViewModal] = useState(false);

  useEffect(() => {
    fetchModules();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleToggleStatus = async (permission) => {
    try {
      const newStatus = permission.status === "active" ? "inactive" : "active";
      if (newStatus === "active") {
        await assignPermission({ permission_id: permission.permissionId });
      } else {
        await revokePermission({ permission_id: permission.permissionId });
      }
      toastSuccess(`Permission ${newStatus} successfully!`);
    } catch (error) {
      toastError(error?.message || "Failed to update permission status");
    }
  };

  const totalItems = pagination?.total ?? permissions.length;
  const totalPages = pagination?.last_page ?? 0;
  const startIndex = (currentPage - 1) * pageSize;

  if (loading && permissions.length === 0) {
    return (
      <div className="flex min-h-[400px] items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <FontAwesomeIcon
            icon={faSpinner}
            className="h-12 w-12 text-blue-600 animate-spin"
          />
          <p className="text-gray-600 text-sm">Loading permissions...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex min-h-[400px] items-center justify-center">
        <div className="text-center">
          <p className="text-red-600 mb-4">
            Failed to load permissions. Please try again.
          </p>
          <Button
            onClick={() => refresh()}
            className="bg-blue-800 hover:bg-blue-900 text-white"
          >
            Retry
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-4 sm:gap-6">
      <ViewPermissionModal
        isOpen={showViewModal}
        onClose={() => {
          setShowViewModal(false);
          setSelectedPermission(null);
        }}
        permission={selectedPermission}
      />

      <Card className="bg-white border border-gray-200 px-4 sm:px-6 py-4 shadow-sm">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h2 className="text-xl sm:text-2xl font-semibold text-gray-900">
              Manage permissions
            </h2>
            <p className="text-xs sm:text-sm text-gray-600 mt-0.5">
              Manage system permissions and access controls
            </p>
          </div>
        </div>
      </Card>

      <Card className="bg-white border border-gray-200 px-4 sm:px-6 py-4 shadow-sm">
        <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
          <SearchBar
            value={searchTerm}
            onChange={(value) => {
              setSearchTerm(value);
              setCurrentPage(1);
            }}
            placeholder="Search by name, module or description..."
            className="w-full lg:w-80"
          />

          <div className="flex flex-wrap items-center gap-2">
            <Select
              value={moduleFilter}
              onValueChange={(value) => {
                setModuleFilter(value);
                setCurrentPage(1);
              }}
            >
              <SelectTrigger className="w-full sm:w-44 h-9 sm:h-10 border border-gray-300 rounded-lg bg-white text-sm">
                <div className="flex items-center gap-2">
                  <FontAwesomeIcon
                    icon={faFilter}
                    className="text-gray-600 h-3 w-3"
                  />
                  <SelectValue placeholder="Module" />
                </div>
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Modules</SelectItem>
                {modules.map((module) => (
                  <SelectItem key={module} value={module}>
                    {module}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
      </Card>

      <Card className="bg-white border border-gray-200 shadow-sm overflow-hidden">
        <div className="px-4 sm:px-6 pt-4 pb-2 border-b border-gray-100">
          <div className="flex items-center justify-between">
            <div className="text-sm text-gray-600">
              Showing{" "}
              <span className="font-semibold text-gray-700">{totalItems}</span>{" "}
              permissions
              {moduleFilter !== "all" && (
                <span className="ml-1 text-blue-600">(filtered)</span>
              )}
            </div>
            <div className="text-sm text-gray-600">
              {permissions.length > 0 && `Page ${currentPage} of ${totalPages}`}
            </div>
          </div>
        </div>

        <div className="overflow-x-auto">
          <Table className="min-w-[800px]">
            <TableHeader>
              <TableRow className="bg-gray-50 hover:bg-gray-50 border-b-2 border-gray-200">
                <TableHead className="text-xs font-semibold text-gray-600 uppercase tracking-wider py-3 px-4">
                  S/N
                </TableHead>
                <TableHead className="text-xs font-semibold text-gray-600 uppercase tracking-wider py-3 px-4">
                  Permission Name
                </TableHead>
                <TableHead className="text-xs font-semibold text-gray-600 uppercase tracking-wider py-3 px-4 hidden md:table-cell">
                  Module
                </TableHead>
                <TableHead className="text-xs font-semibold text-gray-600 uppercase tracking-wider py-3 px-4 hidden lg:table-cell">
                  Description
                </TableHead>
                <TableHead className="text-xs font-semibold text-gray-600 uppercase tracking-wider py-3 px-4">
                  Status
                </TableHead>
                <TableHead className="text-xs font-semibold text-gray-600 uppercase tracking-wider py-3 px-4 text-center">
                  Actions
                </TableHead>
              </TableRow>
            </TableHeader>

            <TableBody>
              {permissions.length > 0 ? (
                permissions.map((permission, index) => {
                  const status = permission.status?.toUpperCase() || "ACTIVE";
                  return (
                    <TableRow
                      key={permission.permissionId || permission.id}
                      className="hover:bg-blue-50/50 transition-colors duration-150 border-b border-gray-100"
                    >
                      <TableCell className="py-3 px-4 text-sm text-gray-600">
                        {startIndex + index + 1}
                      </TableCell>
                      <TableCell className="py-3 px-4">
                        <div>
                          <p className="font-medium text-gray-900 text-sm">
                            {permission.display_name ||
                              permission.permission_name}
                          </p>
                          <p className="text-xs text-gray-600 mt-0.5">
                            {permission.permission_name}
                          </p>
                        </div>
                      </TableCell>
                      <TableCell className="py-3 px-4 hidden md:table-cell">
                        <Badge
                          variant="outline"
                          className="bg-purple-50 text-purple-700 border-purple-200 font-normal text-xs"
                        >
                          {permission.module_name || "N/A"}
                        </Badge>
                      </TableCell>
                      <TableCell className="py-3 px-4 hidden lg:table-cell text-sm text-gray-600 max-w-xs truncate">
                        {permission.description || "No description"}
                      </TableCell>
                      <TableCell className="py-3 px-4">
                        <Badge
                          variant="outline"
                          className={`${
                            status === "ACTIVE"
                              ? "bg-green-50 text-green-700 border-green-200"
                              : "bg-red-50 text-red-700 border-red-200"
                          } border font-normal px-3 py-1 rounded-full text-xs flex items-center gap-1.5 w-fit`}
                        >
                          <span
                            className={`inline-block w-1.5 h-1.5 rounded-full ${
                              status === "ACTIVE"
                                ? "bg-green-500"
                                : "bg-red-500"
                            }`}
                          />
                          {status}
                        </Badge>
                      </TableCell>
                      <TableCell className="py-3 px-4">
                        <div className="flex gap-2 justify-center">
                          <Button
                            size="sm"
                            variant="ghost"
                            className="h-8 w-8 p-0 text-blue-600 hover:bg-blue-50"
                            onClick={() => {
                              setSelectedPermission(permission);
                              setShowViewModal(true);
                            }}
                          >
                            <FontAwesomeIcon icon={faEye} className="h-4 w-4" />
                          </Button>
                          {/* Hide edit and toggle buttons for view-only users */}
                          {false && (
                            <>
                              <Button
                                size="sm"
                                variant="ghost"
                                className="h-8 w-8 p-0 text-green-600 hover:bg-green-50"
                                onClick={() => {
                                  setSelectedPermission(permission);
                                }}
                              >
                                <FontAwesomeIcon
                                  icon={faEdit}
                                  className="h-4 w-4"
                                />
                              </Button>
                              <Button
                                size="sm"
                                variant="ghost"
                                className={`h-8 w-8 p-0 ${
                                  status === "ACTIVE"
                                    ? "text-yellow-600 hover:bg-yellow-50"
                                    : "text-green-600 hover:bg-green-50"
                                }`}
                                onClick={() => handleToggleStatus(permission)}
                              >
                                <FontAwesomeIcon
                                  icon={
                                    status === "ACTIVE"
                                      ? faToggleOff
                                      : faToggleOn
                                  }
                                  className="h-4 w-4"
                                />
                              </Button>
                            </>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })
              ) : (
                <TableRow>
                  <TableCell colSpan={6} className="text-center py-8">
                    <EmptyState
                      title="No permissions found"
                      message={
                        searchTerm || moduleFilter !== "all"
                          ? "Try adjusting your search or filters"
                          : "Add your first permission to get started"
                      }
                    />
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </div>

        {totalItems > 0 && totalPages > 0 && (
          <div className="px-4 sm:px-6 py-3 border-t border-gray-100">
            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              pageSize={pageSize}
              totalItems={totalItems}
              onPageChange={setCurrentPage}
              onPageSizeChange={setPageSize}
            />
          </div>
        )}
      </Card>
    </div>
  );
};

export default ManagePermissionA;
